/** @typedef {import("../../../api/types").Product} Product */
import React from "react";
import {Button, FormGroup, Input, Label, ListGroup, ListGroupItem} from "reactstrap";
import SearchSelect from "../../../shared/components/SearchSelect";
import {formatMoney} from "../../../shared/utils/format";
import FormModal from "../../../shared/components/FormModal";
import {ItemSummary} from "./OrderSummaries";
import RowActions from "../../../shared/components/RowActions";
import {useRowEditor} from "../../../hooks/useRowEditor";
import IconLabel from "../../../shared/components/IconLabel";
import {PlusIcon} from "../../../shared/components/icons";

/**
 * @typedef {{key: number, productId: string, quantity: string}} ProductRow
 * @param {{
 *   rows: ProductRow[],
 *   products: Product[],
 *   currency?: string,
 *   errors: Record<number, Record<string, string>>,
 *   onChange: (rows: ProductRow[]) => void,
 * }} props
 */
const OrderProductsEditor = ({rows, products, currency, errors, onChange}) => {
    const editor = useRowEditor(rows, onChange);
    const byId = new Map(products.map((product) => [String(product.id), product]));

    return (
        <>
            {rows.length === 0 && <p className="text-muted">Nenhum produto adicionado.</p>}
            {rows.length > 0 && (
                <ListGroup className="mb-3">
                    {rows.map((row, index) => {
                        const product = byId.get(row.productId);
                        const quantity = Number(row.quantity);
                        const unit = Number(product?.base_value);
                        return (
                            <ListGroupItem className="d-flex align-items-start gap-2" key={row.key}>
                                <ItemSummary
                                    name={product?.name}
                                    quantity={quantity}
                                    unit={unit}
                                    total={unit * quantity}
                                    currency={currency}
                                    errors={Object.values(errors[index] ?? {})}
                                />
                                <RowActions
                                    noun="produto"
                                    onEdit={() => editor.edit(row.key)}
                                    onRemove={() => editor.remove(row.key)}
                                />
                            </ListGroupItem>
                        );
                    })}
                </ListGroup>
            )}
            <Button color="primary" outline size="sm" onClick={editor.add}>
                <IconLabel icon={<PlusIcon />}>Adicionar produto</IconLabel>
            </Button>

            <FormModal
                isOpen={editor.isOpen}
                toggle={editor.close}
                title={editor.current ? "Editar produto" : "Adicionar produto"}
                initial={{
                    productId: editor.current?.productId ?? "",
                    quantity: editor.current?.quantity ?? "1",
                }}
                canSave={(v) => Boolean(byId.has(v.productId) && Number(v.quantity) >= 1)}
                onSave={editor.save}
            >
                {(values, set) => (
                    <>
                        <FormGroup>
                            <Label for="product-select">Produto</Label>
                            <SearchSelect
                                id="product-select"
                                autoFocus
                                ariaLabel="Produto"
                                placeholder="Buscar produto…"
                                emptyMessage="Nenhum produto encontrado."
                                value={values.productId}
                                onChange={(productId) => set({productId})}
                                options={products.map((p) => ({
                                    value: String(p.id),
                                    label: `${p.name} — ${formatMoney(p.base_value, p.currency)}`,
                                    search: p.description ?? "",
                                }))}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="product-quantity">Quantidade</Label>
                            <Input
                                id="product-quantity"
                                type="number"
                                min="1"
                                step="1"
                                value={values.quantity}
                                onChange={(e) => set({quantity: e.target.value})}
                            />
                        </FormGroup>
                    </>
                )}
            </FormModal>
        </>
    );
};

export default OrderProductsEditor;
